import React, {
  memo,
  useMemo,
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react';
import { Box, Typography, IconButton, TextField, Button, useTheme } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EmailIcon from '@mui/icons-material/Email';
import TelegramIcon from '@mui/icons-material/Telegram';
import RedditIcon from '@mui/icons-material/Reddit';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import { ModalContainer } from 'components/Modals/ModalContainer';
import { ReactComponent as XIcon } from 'assets/images/icons/shareIcons/icon-x.svg';
import { SHARE_URLS } from 'shared/constants/share-urls.constants';
import { useAppDispatch, useAppSelector } from 'redux/hooks/reduxHooks';
import { selectShareModal } from 'redux/selectors/selectors';
import { closeShareModal } from 'redux/slices/modals/modals.slice';

interface SharePlatform {
  id: string;
  name: string;
  icon: React.ReactNode;
  bgColor: string;
  hoverColor: string;
  onClick: () => void;
}

const SCROLL_AMOUNT = 200;
const COPY_TIMEOUT = 2000;
const ARROW_UPDATE_DELAY = 100;
const DEFAULT_TITLE = 'Check out this debate on Deb8';

export const ShareModal: React.FC = memo(() => {
  const dispatch = useAppDispatch();
  const { isOpen, shareUrl, title } = useAppSelector(selectShareModal);
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    dispatch(closeShareModal());
  };

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
  }, [shareUrl]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (copied) {
      timer = setTimeout(() => {
        setCopied(false);
      }, COPY_TIMEOUT);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [copied]);

  const handleClipboardWrite = useCallback((text: string) => {
    try {
      navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  const openShareWindow = useCallback((url: string, name: string, windowFeatures: string) => {
    try {
      window.open(url, name, windowFeatures);
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  const handleShareActions = useMemo(
    () => ({
      email: () => {
        try {
          const subject = encodeURIComponent(title || DEFAULT_TITLE);
          const body = encodeURIComponent(title ? `${title}\n\n${shareUrl}` : shareUrl);
          const emailUrl = `mailto:?subject=${subject}&body=${body}`;
          const tempLink = document.createElement('a');
          tempLink.href = emailUrl;
          tempLink.target = '_blank';
          document.body.appendChild(tempLink);
          tempLink.click();
          document.body.removeChild(tempLink);
          handleClipboardWrite(shareUrl);
        } catch (error) {
          handleClipboardWrite(shareUrl);
        }
      },
      telegram: () => {
        try {
          const shareText = title ? `${title}\n${shareUrl}` : shareUrl;
          handleClipboardWrite(shareText);

          const telegramText = title || '';
          const telegramUrl = `${SHARE_URLS.telegram}?text=${encodeURIComponent(
            shareUrl,
          )}&url=${encodeURIComponent(telegramText)}`;

          const width = 550;
          const height = 470;
          const left = window.screen.width / 2 - width / 2;
          const top = window.screen.height / 2 - height / 2;

          openShareWindow(
            telegramUrl,
            'telegram-share-dialog',
            `width=${width},height=${height},top=${top},left=${left}`,
          );
        } catch (error) {
          const shareText = title ? `${title}\n${shareUrl}` : shareUrl;
          handleClipboardWrite(shareText);
        }
      },
      x: () => {
        try {
          const shareText = title
            ? `${title}\n${shareUrl}\nvia @Deb8TV (https://x.com/Deb8tv)`
            : `${shareUrl}\nvia @Deb8TV (https://x.com/Deb8tv)`;

          handleClipboardWrite(shareText);

          const viaText = '\n\nvia @Deb8TV';
          const twitterUrl = title
            ? `${SHARE_URLS.twitter}?text=${encodeURIComponent(`${title}\n${shareUrl}${viaText}`)}`
            : `${SHARE_URLS.twitter}?text=${encodeURIComponent(`${shareUrl}${viaText}`)}`;

          const width = 550;
          const height = 420;
          const left = window.screen.width / 2 - width / 2;
          const top = window.screen.height / 2 - height / 2;

          openShareWindow(
            twitterUrl,
            'twitter-share-dialog',
            `width=${width},height=${height},top=${top},left=${left},toolbar=0,location=0,menubar=0`,
          );
        } catch (error) {
          const shareText = title
            ? `${title}\n${shareUrl}\nvia @Deb8TV (https://x.com/Deb8tv)`
            : `${shareUrl}\nvia @Deb8TV (https://x.com/Deb8tv)`;

          handleClipboardWrite(shareText);
        }
      },
      whatsapp: () => {
        try {
          const whatsappText = title ? `${title}\n\n${shareUrl}` : shareUrl;
          handleClipboardWrite(whatsappText);

          const whatsappUrl = `${SHARE_URLS.whatsapp}?text=${encodeURIComponent(whatsappText)}`;
          openShareWindow(whatsappUrl, '_blank', '');
        } catch (error) {
          handleClipboardWrite(shareUrl);
        }
      },
      reddit: () => {
        try {
          const redditTitle = title || DEFAULT_TITLE;
          const redditUrl = `${SHARE_URLS.reddit}?url=${encodeURIComponent(
            shareUrl,
          )}&title=${encodeURIComponent(redditTitle)}`;

          const width = 700;
          const height = 550;
          const left = window.screen.width / 2 - width / 2;
          const top = window.screen.height / 2 - height / 2;

          openShareWindow(
            redditUrl,
            'reddit-share-dialog',
            `width=${width},height=${height},top=${top},left=${left},toolbar=0,location=0,menubar=0`,
          );
        } catch (error) {
          handleClipboardWrite(shareUrl);
        }
      },
    }),
    [shareUrl, title, handleClipboardWrite, openShareWindow],
  );

  const createSharePlatforms = useCallback(
    (actions: Record<string, () => void>): SharePlatform[] => [
      {
        id: 'x',
        name: 'X',
        icon: <Box component={XIcon} sx={{ width: '28px', height: '28px' }} />,
        bgColor: theme.palette.common.black,
        hoverColor: theme.palette.grey[800],
        onClick: actions.x,
      },
      {
        id: 'email',
        name: 'Email',
        icon: <EmailIcon sx={{ fontSize: 32 }} />,
        bgColor: theme.palette.error.main,
        hoverColor: theme.palette.error.dark,
        onClick: actions.email,
      },
      {
        id: 'telegram',
        name: 'Telegram',
        icon: <TelegramIcon sx={{ fontSize: 32 }} />,
        bgColor: theme.palette.mySocial.telegram,
        hoverColor: theme.palette.mySocial.telegramDark,
        onClick: actions.telegram,
      },
      {
        id: 'whatsapp',
        name: 'WhatsApp',
        icon: <WhatsAppIcon sx={{ fontSize: 32 }} />,
        bgColor: '#25D366',
        hoverColor: '#128C7E',
        onClick: actions.whatsapp,
      },
      {
        id: 'reddit',
        name: 'Reddit',
        icon: <RedditIcon sx={{ fontSize: 32 }} />,
        bgColor: theme.palette.mySocial.reddit,
        hoverColor: theme.palette.mySocial.redditDark,
        onClick: actions.reddit,
      },
    ],
    [theme],
  );

  const sharePlatforms = useMemo(
    () => createSharePlatforms(handleShareActions),
    [createSharePlatforms, handleShareActions],
  );

  const getIconButtonStyle = useCallback(
    (bgColor: string, hoverColor: string) => ({
      backgroundColor: bgColor,
      borderRadius: '8px',
      padding: '16px',
      width: '64px',
      height: '64px',
      color: theme.palette.common.white,
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: hoverColor,
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
      },
      flexShrink: 0,
    }),
    [theme.palette.common.white],
  );

  const updateArrows = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, clientWidth, scrollWidth } = container;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    setShowLeft(!atStart);
    setShowRight(!atEnd);
  }, []);

  const scrollByAmount = useCallback(
    (amount: number) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      if (amount > 0) {
        setShowLeft(true);
      } else {
        setShowRight(true);
      }

      container.scrollBy({ left: amount, behavior: 'smooth' });
      setTimeout(updateArrows, ARROW_UPDATE_DELAY);
    },
    [updateArrows],
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', updateArrows);
    window.addEventListener('resize', updateArrows);

    return () => {
      container.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);

  useLayoutEffect(() => {
    if (isOpen) {
      requestAnimationFrame(updateArrows);
    }
  }, [isOpen, updateArrows]);

  const navButtonStyle = useMemo(
    () => ({
      position: 'absolute' as const,
      top: '50%',
      transform: 'translateY(-50%)',
      backgroundColor: theme.palette.common.white,
      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
      zIndex: 2,
      transition: 'opacity 0.3s ease',
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      },
      '&:active': {
        backgroundColor: theme.palette.grey[200],
        transform: 'translateY(-50%) translateY(1px)',
      },
    }),
    [theme],
  );

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      padding={{ lg: '32px', md: '24px', sm: '24px', xs: '16px' }}
    >
      <Box>
        <Typography
          variant="h6"
          mb={3}
          sx={{
            fontWeight: 400,
            fontSize: '20px',
            lineHeight: '28px',
            color: theme.palette.text.primary,
          }}
        >
          Share
        </Typography>

        <Box sx={{ position: 'relative', mb: 3 }}>
          <IconButton
            onClick={() => scrollByAmount(-SCROLL_AMOUNT)}
            sx={{
              ...navButtonStyle,
              left: '-20px',
              opacity: showLeft ? 1 : 0,
              pointerEvents: showLeft ? 'auto' : 'none',
            }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>

          <IconButton
            onClick={() => scrollByAmount(SCROLL_AMOUNT)}
            sx={{
              ...navButtonStyle,
              right: '-20px',
              opacity: showRight ? 1 : 0,
              pointerEvents: showRight ? 'auto' : 'none',
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>

          <Box
            ref={scrollContainerRef}
            sx={{
              display: 'flex',
              gap: '8px',
              overflowX: 'auto',
              overflowY: 'hidden',
              py: 1,
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {sharePlatforms.map((platform) => (
              <Box
                key={platform.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <IconButton
                  onClick={platform.onClick}
                  sx={getIconButtonStyle(platform.bgColor, platform.hoverColor)}
                  aria-label={`Share on ${platform.name}`}
                >
                  {platform.icon}
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                    userSelect: 'none',
                  }}
                >
                  {platform.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
          <TextField
            fullWidth
            value={shareUrl}
            InputProps={{
              readOnly: true,
              sx: {
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: theme.palette.grey[50],
                border: `1px solid ${theme.palette.grey[300]}`,
                '&:hover': {
                  backgroundColor: theme.palette.grey[100],
                },
              },
            }}
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleCopy}
            sx={{
              borderRadius: '8px',
              backgroundColor: copied ? theme.palette.success.main : theme.palette.common.black,
              color: theme.palette.common.white,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '16px',
              px: 3,
              minWidth: '100px',
              height: '40px',
              '&:hover': {
                backgroundColor: copied ? theme.palette.success.dark : theme.palette.grey[800],
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
              '&:active': {
                transform: 'translateY(1px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </Box>
      </Box>
    </ModalContainer>
  );
});
